import { useContext, useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Table, Form, Badge, Button } from "react-bootstrap";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import crudApi from "@serie3/common/api/crudApi";
import AdminEndpoints from "@serie3/mymediaplanner/Modules/Admin/Resources/Endpoints";
import NotFoundPage from "../../../shared/404";
import NewModal from "../../../shared/Modals/NewModal";
import SaveButton from "../../../shared/Buttons/SaveButton";

// Must match the `system_groups` parameter of the API. Empty value = all groups.
const SYSTEM_GROUPS = ["General", "Profile", "Settings", "Document", "GroupCreator"];

const today = () => new Date().toISOString().slice(0, 10);

const addMonths = (months) => {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toISOString().slice(0, 10);
};

const isActive = (paidTil) => !!paidTil && paidTil >= today();

// Not activated = signed up but never confirmed the mail; such users have no
// system groups yet, so nothing else about them (paid period etc.) applies.
const PaidBadge = ({ paidTil, roles, emailValidated }) => {
  const hasRole = roles.includes("ROLE_PAYED_USER");
  if (emailValidated === false) {
    return <Badge bg="danger">not activated</Badge>;
  }
  if (isActive(paidTil)) {
    return <Badge bg="success">paid until {paidTil}</Badge>;
  }
  if (hasRole) {
    // Role persisted but no running period: Stripe-granted or never expired.
    return <Badge bg="warning" text="dark">role only{paidTil ? ` (expired ${paidTil})` : ""}</Badge>;
  }
  return <Badge bg="secondary">free{paidTil ? ` (expired ${paidTil})` : ""}</Badge>;
};

const RoleBadges = ({ roles }) => (
  <>
    {roles
      .filter((r) => r !== "ROLE_USER")
      .map((r) => (
        <Badge key={r} bg="light" text="dark" className="me-1">
          {r.replace("ROLE_", "")}
        </Badge>
      ))}
  </>
);

// Rank for sorting the status column: not activated < free < role only < paid.
// Paid users additionally order by their paid-until date.
const statusRank = (u) => {
  if (u.email_validated === false) return 0;
  if (isActive(u.paid_til)) return 3;
  if (u.roles.includes("ROLE_PAYED_USER")) return 2;
  return 1;
};

const SORTERS = {
  user_id: (a, b) => a.user_id - b.user_id,
  email: (a, b) => (a.email ?? "").localeCompare(b.email ?? ""),
  status: (a, b) => statusRank(a) - statusRank(b) || (a.paid_til ?? "").localeCompare(b.paid_til ?? ""),
  roles: (a, b) => a.roles.length - b.roles.length || a.roles.join().localeCompare(b.roles.join()),
  post_count: (a, b) => (a.post_count ?? 0) - (b.post_count ?? 0),
};

const SortableHeader = ({ column, label, sort, onSort, className = "" }) => {
  const active = sort.column === column;
  return (
    <th
      role="button"
      className={`user-select-none text-nowrap ${className}`}
      aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      onClick={() => onSort(column)}
    >
      {label}
      <span className={active ? "ms-1" : "ms-1 text-muted opacity-50"}>
        {active ? (sort.dir === "asc" ? "\u25B2" : "\u25BC") : "\u25B4\u25BE"}
      </span>
    </th>
  );
};

/**
 * Modal to set a user's paid period by hand. Writes Group::paidTil on the API
 * and lets the API decide about ROLE_PAYED_USER (future date grants it, past
 * date revokes it), then hands the updated row back via onSaved.
 */
const PaidTilModal = ({ user, show, setShow, onSaved }) => {
  const [paidTil, setPaidTil] = useState(today());
  const [group, setGroup] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !user) return;
    setPaidTil(isActive(user.paid_til) ? user.paid_til : addMonths(3));
    setGroup("");
    setError(null);
  }, [show, user]);

  const save = async () => {
    if (!user || !paidTil) return;
    setSaving(true);
    setError(null);
    const body = { paid_til: paidTil };
    if (group) body.group = group;

    const response = await crudApi.edit(AdminEndpoints.users.paidTil(user.user_id), body);
    setSaving(false);

    if (response.status === 200) {
      onSaved(user.user_id, response.data);
      setShow(false);
    } else {
      const data = response.data;
      const message = Array.isArray(data)
        ? data.map((e) => e.message).join(", ")
        : data?.message || data?.detail || `Request failed (${response.status})`;
      setError(message);
    }
  };

  const Save = () => <SaveButton submitFunction={save} isSaving={saving} buttonText="Set paid until" />;

  return (
    <NewModal show={show} setShow={setShow} title={user ? `Paid period: ${user.email}` : ""} ExtraButton={Save} size="md">
      {user && (
        <Form onSubmit={(e) => { e.preventDefault(); save(); }}>
          <Form.Group className="mb-3">
            <Form.Label>Paid until</Form.Label>
            <Form.Control type="date" value={paidTil} onChange={(e) => setPaidTil(e.target.value)} />
            <div className="mt-2">
              {[1, 3, 6, 12].map((m) => (
                <Button key={m} size="sm" variant="outline-secondary" className="me-1" onClick={() => setPaidTil(addMonths(m))}>
                  +{m} {m === 1 ? "month" : "months"}
                </Button>
              ))}
              <Button size="sm" variant="outline-danger" onClick={() => setPaidTil(addMonths(-1))}>
                expire (1 month ago)
              </Button>
            </div>
            <Form.Text muted>
              A date today or later grants ROLE_PAYED_USER, a past date revokes it.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>System group</Form.Label>
            <Form.Select value={group} onChange={(e) => setGroup(e.target.value)}>
              <option value="">All system groups</option>
              {SYSTEM_GROUPS.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </Form.Select>
          </Form.Group>

          {error && <div className="text-danger">{error}</div>}
        </Form>
      )}
    </NewModal>
  );
};

const AdminUsersInner = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [activatingId, setActivatingId] = useState(null);
  const [sort, setSort] = useState({ column: "email", dir: "asc" });

  const onSort = (column) =>
    setSort((prev) =>
      prev.column === column ? { column, dir: prev.dir === "asc" ? "desc" : "asc" } : { column, dir: "asc" }
    );

  useEffect(() => {
    const load = async () => {
      const response = await crudApi.justGet(AdminEndpoints.users.list);
      if (response.status === 200 && Array.isArray(response.data)) {
        setUsers(response.data.map((u) => ({ ...u, roles: u.roles ?? [] })));
      }
      setLoading(false);
    };
    load();
  }, []);

  const visible = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = needle
      ? users.filter((u) => (u.email ?? "").toLowerCase().includes(needle) || String(u.user_id) === needle)
      : users;
    const compare = SORTERS[sort.column] ?? SORTERS.email;
    const sign = sort.dir === "asc" ? 1 : -1;
    // Tie-break on email so the order is stable across re-renders.
    return [...list].sort((a, b) => sign * compare(a, b) || SORTERS.email(a, b));
  }, [users, filter, sort]);

  const onSaved = (userId, result) => {
    setUsers((prev) =>
      prev.map((u) => {
        if (u.user_id !== userId) return u;
        // paid_til in the table is the General group's; only update it when
        // that group was part of the change.
        const touchedGeneral = (result.groups ?? []).some((g) => g.endsWith("_General_Group"));
        return {
          ...u,
          roles: result.roles ?? u.roles,
          paid_til: touchedGeneral ? result.paid_til : u.paid_til,
        };
      })
    );
  };

  const activate = async (user) => {
    if (!window.confirm(`Activate ${user.email}? This validates the email and creates the system groups.`)) return;
    setActivatingId(user.user_id);
    const response = await crudApi.createNew(AdminEndpoints.users.activate(user.user_id), {});
    setActivatingId(null);
    if (response.status === 200) {
      setUsers((prev) =>
        prev.map((u) =>
          u.user_id === user.user_id
            ? { ...u, email_validated: true, roles: response.data?.roles ?? u.roles }
            : u
        )
      );
    }
  };

  const paidCount = users.filter((u) => isActive(u.paid_til)).length;
  const inactiveCount = users.filter((u) => u.email_validated === false).length;

  return (
    <>
      <PaidTilModal user={editUser} show={showModal} setShow={setShowModal} onSaved={onSaved} />

      <Row className="mb-3">
        <Col md={3}>
          <div className="card"><div className="card-body">
            <h4 className="mb-0">{users.length}</h4>
            <div className="text-muted font-size-12">Users</div>
          </div></div>
        </Col>
        <Col md={3}>
          <div className="card"><div className="card-body">
            <h4 className="mb-0" style={{ color: "#34c38f" }}>{paidCount}</h4>
            <div className="text-muted font-size-12">With running paid period</div>
          </div></div>
        </Col>
        <Col md={2}>
          <div className="card"><div className="card-body">
            <h4 className="mb-0" style={{ color: inactiveCount ? "#f46a6a" : undefined }}>{inactiveCount}</h4>
            <div className="text-muted font-size-12">Not activated</div>
          </div></div>
        </Col>
        <Col md={4}>
          <Form.Control
            placeholder="Filter by email or user id"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </Col>
      </Row>

      <Row>
        <Col>
          <Card>
            <Card.Body>
              {loading ? (
                <div className="text-muted">Loading users…</div>
              ) : (
                <Table hover responsive size="sm" className="align-middle">
                  <thead>
                    <tr>
                      <SortableHeader column="user_id" label="ID" sort={sort} onSort={onSort} />
                      <SortableHeader column="email" label="Email" sort={sort} onSort={onSort} />
                      <SortableHeader column="status" label="Status" sort={sort} onSort={onSort} />
                      <SortableHeader column="roles" label="Roles" sort={sort} onSort={onSort} />
                      <SortableHeader column="post_count" label="Posts" sort={sort} onSort={onSort} className="text-end" />
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((u) => (
                      <tr key={u.user_id}>
                        <td>{u.user_id}</td>
                        <td>{u.email}</td>
                        <td><PaidBadge paidTil={u.paid_til} roles={u.roles} emailValidated={u.email_validated} /></td>
                        <td><RoleBadges roles={u.roles} /></td>
                        <td className="text-end">{u.post_count}</td>
                        <td className="text-end text-nowrap">
                          {u.email_validated === false && (
                            <Button
                              size="sm"
                              variant="success"
                              className="me-1"
                              disabled={activatingId === u.user_id}
                              onClick={() => activate(u)}
                            >
                              {activatingId === u.user_id ? "Activating…" : "Activate"}
                            </Button>
                          )}
                          <Button size="sm" variant="outline-primary" onClick={() => { setEditUser(u); setShowModal(true); }}>
                            Set paid until
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {visible.length === 0 && (
                      <tr><td colSpan={6} className="text-muted">No users match.</td></tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );
};

/**
 * Super-admin user management. Hides the UI only; the API enforces
 * ROLE_SUPER_ADMIN on every endpoint used here.
 */
const AdminUsers = () => {
  const { settings } = useContext(SettingsContext);

  if (!settings?.user?.roles) return null;

  const roles = Object.values(settings.user.roles);
  if (!roles.includes("ROLE_SUPER_ADMIN")) return <NotFoundPage />;

  return <AdminUsersInner />;
};

export default AdminUsers;
